// @mui
import { alpha, styled } from '@mui/material/styles';
import { ListItemText, ListItemButton, ListItemIcon } from '@mui/material';
// sidebar palette
import {
  SIDEBAR_FONT,
  SIDEBAR_METRICS
} from '../../../layouts/dashboard/navbar/sidebarTokens';
import { UNIVERSE } from '../../../theme/universe/brand';

// ----------------------------------------------------------------------
// Nav item styles for the sidebar. Colours come from CSS custom properties set
// on the drawer (see sidebarTokens.js), which is how these generic components
// follow both the app's light/dark mode and the portal's hue without props.
//
// EVERY var() carries a fallback. The design used to depend on those custom
// properties actually reaching the item from an ancestor; when that failed
// (measured in a render harness) the active tile lost its fill and its white
// icon vanished against the light ground — "the icon is invisible on the
// current page". With a fallback the tile is always coloured and the icon
// always legible, whether or not the variables cascade. The fallbacks are
// theme-aware for the neutrals and the brand indigo for the accent, so the
// worst case is the Team look rather than a broken one.
//
// Three states, each with one job:
//   rest     soft label, icon in a glass tile
//   hover    the tile brightens and the label goes full ink — nothing else moves
//   active   a beacon on the panel's edge, the tile filled with the portal's
//            gradient (the brand's on the Team rail), and a whisper of the
//            accent behind the label
// ----------------------------------------------------------------------

/**
 * Fallback values for every custom property the item reads.
 *
 * The rail is ALWAYS the deep-space cosmos — NavbarVertical hard-wires
 * SIDEBAR_MODE to 'dark' whatever the app's light/dark theme — so every
 * fallback here is the DARK palette: white ink on the night, never the app
 * theme's. When the `--sb-*` variables cascade from the drawer (the normal
 * case) the portal's own colours win; when they somehow don't, the item stays
 * white-on-dark and legible. The old fallback resolved to the LIGHT theme's
 * near-black ink, which on the dark rail is invisible — that was the "white
 * sidebar / icon invisible on the current page" failure. A fixed dark fallback
 * cannot reproduce it. The accent falls back to the Universe brand (the
 * light blue, the gradient's midpoint, the gradient), so the worst case is
 * the brand's own hue rather than a broken one.
 */
function fb() {
  return {
    text: 'var(--sb-text, #FFFFFF)',
    textSoft: 'var(--sb-text-soft, rgba(255,255,255,0.68))',
    textMuted: 'var(--sb-text-muted, rgba(255,255,255,0.42))',
    glass: 'var(--sb-glass, rgba(255,255,255,0.05))',
    glassStrong: 'var(--sb-glass-strong, rgba(255,255,255,0.10))',
    glassBorder: 'var(--sb-glass-border, rgba(255,255,255,0.09))',
    glassBorderStrong: 'var(--sb-glass-border-strong, rgba(255,255,255,0.16))',
    accent: `var(--sb-accent, ${UNIVERSE.mid})`,
    beacon: `var(--sb-beacon, ${UNIVERSE.light})`,
    accentTint: `var(--sb-accent-tint, ${alpha(UNIVERSE.light, 0.22)})`,
    glow: `var(--sb-glow, ${alpha(UNIVERSE.light, 0.45)})`,
    // the active tile wears the brand gradient (DESIGN.md §12)
    gradient: `var(--sb-gradient, ${UNIVERSE.gradient})`,
    activeBg: 'var(--sb-active-bg, rgba(255,255,255,0.04))',
    activeBgHover: 'var(--sb-active-bg-hover, rgba(255,255,255,0.06))',
    activeSubBg: 'var(--sb-active-sub-bg, rgba(255,255,255,0.05))'
  };
}

export const ListItemStyle = styled(ListItemButton, {
  shouldForwardProp: (prop) =>
    !['activeRoot', 'activeSub', 'subItem', 'isCollapse'].includes(prop)
})(({ activeRoot, activeSub, subItem, isCollapse, theme }) => {
  const c = fb();
  return {
    position: 'relative',
    height: SIDEBAR_METRICS.itemHeight,
    paddingLeft: SIDEBAR_METRICS.itemPaddingLeft,
    paddingRight: theme.spacing(1.5),
    marginBottom: SIDEBAR_METRICS.itemGap,
    borderRadius: 12,
    fontFamily: SIDEBAR_FONT,
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.3,
    textTransform: 'capitalize',
    color: c.textSoft,
    // Only the text colour eases. The hover BACKGROUND is left to change
    // instantly: it is a wide, translucent fill composited over the rail's
    // five-layer cosmic background, and animating it repaints that whole stack
    // every frame — which is the "input lag" when the pointer sweeps the list.
    // An instant fill is one repaint, and reads as more responsive, not less.
    transition: theme.transitions.create('color', {
      duration: theme.transitions.duration.shorter
    }),

    // The beacon: a short bar on the very edge of the panel, not inside the
    // pill, so it reads as "you are here" the way a tab marker does.
    '&::before': {
      content: '""',
      position: 'absolute',
      left: -16,
      top: '50%',
      width: 3,
      height: 0,
      borderRadius: 3,
      transform: 'translateY(-50%)',
      background: c.beacon,
      boxShadow: `0 0 14px ${c.glow}`,
      transition: theme.transitions.create('height', {
        duration: theme.transitions.duration.short
      })
    },

    // The tile lifts on hover — but only for root items, which have a tile.
    // The label is deliberately NOT nudged: on a sub-item (a dot + label, no
    // tile) a shifting label slid out of line with its dot and read as
    // misaligned. Sub-items get the clean background + colour change only.
    '&:hover': {
      color: c.text,
      backgroundColor: c.glass,
      ...(!subItem && {
        '& .sb-tile': {
          backgroundColor: c.glassStrong,
          borderColor: c.glassBorderStrong,
          transform: 'translateY(-1px) scale(1.06)'
        }
      })
    },
    '&.Mui-focusVisible': {
      outline: `2px solid ${c.beacon}`,
      outlineOffset: -2
    },

    // activeRoot
    ...(activeRoot && {
      color: c.text,
      backgroundColor: c.activeBg,
      backgroundImage: `linear-gradient(90deg, ${c.accentTint} 0%, rgba(255,255,255,0) 72%)`,
      '&::before': { height: 22 },
      '&:hover': { backgroundColor: c.activeBgHover },
      '& .sb-tile, &:hover .sb-tile': {
        color: '#FFFFFF',
        backgroundColor: c.accent,
        backgroundImage: c.gradient,
        borderColor: 'transparent',
        transform: 'scale(1.04)',
        boxShadow: `0 10px 22px ${c.glow}`
      }
    }),

    // subItem
    ...(subItem && {
      height: SIDEBAR_METRICS.subItemHeight,
      marginBottom: 2,
      paddingLeft: 16,
      borderRadius: 10,
      fontSize: '0.8125rem',
      fontWeight: 500,
      '&::before': { display: 'none' }
    }),

    // activeSub
    ...(activeSub && {
      color: c.text,
      fontWeight: 600,
      backgroundColor: c.activeSubBg
    }),

    // collapsed rail: the tile alone, centred. No edge beacon here — it is a
    // marker for a labelled row, and at 88px it floats awkwardly off the tile;
    // the filled tile alone carries the active state.
    //
    // The tile's right margin goes with the label. It is the gap BETWEEN the
    // two, and with the label gone it was still pushing the tile 6px off the
    // rail's centre line — which, with the label's flex-grow below, left the
    // whole nav column 13px to the left of the logo and the avatar.
    ...(isCollapse && {
      justifyContent: 'center',
      paddingLeft: 0,
      paddingRight: 0,
      '& .sb-tile': { marginRight: 0 },
      '&::before': { display: 'none' }
    })
  };
});

export const ListItemTextStyle = styled(ListItemText, {
  shouldForwardProp: (prop) => prop !== 'isCollapse'
})(({ isCollapse, theme }) => ({
  margin: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  transition: theme.transitions.create(['width', 'opacity'], {
    duration: theme.transitions.duration.shorter
  }),
  // `width: 0` is not enough to take the label out of the layout: a MUI
  // ListItemText root is `flex: 1 1 auto`, so it kept GROWING into whatever
  // space the tile left over. That filled the row, `justify-content: center`
  // had nothing left to distribute, and the tile stayed pinned to the left
  // edge — the nav column sitting 13px off the logo and the avatar above it.
  ...(isCollapse && {
    width: 0,
    flexGrow: 0,
    flexBasis: 0,
    opacity: 0
  })
}));

// The icon tile. Icons inherit `currentColor`, so the tile's `color` is the
// icon's colour: a strong ink at rest, white on the accent when active.
export const ListItemIconStyle = styled(ListItemIcon)(({ theme }) => {
  const c = fb();
  return {
    minWidth: 0,
    width: SIDEBAR_METRICS.tile,
    height: SIDEBAR_METRICS.tile,
    marginRight: 12,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    // A touch stronger than the label so the duotone glyphs stay crisp on the
    // faint tile rather than washing out. Dark fallback: the rail is always the
    // cosmos, so an unresolved variable must still leave a bright glyph.
    color: 'var(--sb-text-soft, rgba(255,255,255,0.80))',
    backgroundColor: c.glass,
    border: `1px solid ${c.glassBorder}`,
    // Only the transform (a GPU-composited scale) animates; the fill, border,
    // shadow and colour snap. That keeps the hover cheap on the main thread.
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shorter
    }),
    willChange: 'transform',
    '& svg, & span': { width: 21, height: 21 }
  };
});
